if (!window.Maximize.loadedScript.includes('multi-location-inventory.js')) {
  window.Maximize.loadedScript.push('multi-location-inventory.js');

  class MultixStockAvailability extends HTMLElement {
    constructor() {
      super();
      this.config = {};
    }

    connectedCallback() {
      this.config = {
        blockId: this.dataset.blockId,
        sectionId: this.dataset.sectionId,
        token: this.dataset.storefrontToken,
        domain: this.dataset.shopDomain,
        handle: this.dataset.productHandle,
        rigasName: this.dataset.rigasLocation,
        shopName: this.dataset.shopLocation,
        qtySuffix: this.dataset.qtySuffix,
        rigasLabel: this.dataset.rigasLabel,
        shopLabel: this.dataset.shopLabel,
        rigasDelivery: this.dataset.rigasDelivery,
        shopDelivery: this.dataset.shopDelivery,
        noStockMsg: this.dataset.noStockMsg,
        availableText: this.dataset.availableText,
        inventoryQuantity: parseInt(this.dataset.inventoryQuantity, 10) || 0
      };

      if (this.config.token) {
        this.fetchAndRender(this.dataset.variantId);
      }

      this.listenForVariantChanges();
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    normalizeLocation(name) {
      return String(name || '').trim().toLowerCase();
    }

    matchLocation(name, configuredName, fallbackKeyword) {
      const normalizedName = this.normalizeLocation(name);
      const normalizedConfigured = this.normalizeLocation(configuredName);

      return (
        normalizedName === normalizedConfigured ||
        normalizedName.includes(fallbackKeyword)
      );
    }

    async fetchVariantAvailability(variantId) {
      if (!this.config.token || !this.config.domain || !variantId) return null;

      const query = `
        query VariantStock($id: ID!) {
          node(id: $id) {
            ... on ProductVariant {
              quantityAvailable
              storeAvailability(first: 10) {
                nodes {
                  available
                  quantityAvailable
                  location { name }
                }
              }
            }
          }
        }
      `;

      try {
        const response = await fetch(`https://${this.config.domain}/api/2025-01/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.config.token
          },
          body: JSON.stringify({
            query,
            variables: {
              id: `gid://shopify/ProductVariant/${variantId}`
            }
          })
        });

        const payload = await response.json();

        if (payload.errors?.length) {
          console.warn('[MultixStockAvailability] Storefront API unavailable:', payload.errors);
          return null;
        }

        const variant = payload?.data?.node;
        if (!variant) return null;

        const nodes = variant.storeAvailability?.nodes || [];
        const result = { rigas: null, shop: null };

        for (const availability of nodes) {
          if (this.matchLocation(availability.location.name, this.config.rigasName, 'rig')) {
            result.rigas = {
              available: availability.available,
              qty: availability.quantityAvailable
            };
          } else if (this.matchLocation(availability.location.name, this.config.shopName, 'shop')) {
            result.shop = {
              available: availability.available,
              qty: availability.quantityAvailable
            };
          }
        }

        if (!result.rigas && !result.shop && variant.quantityAvailable > 0) {
          result.rigas = {
            available: true,
            qty: variant.quantityAvailable
          };
        }

        if (!result.rigas) {
          result.rigas = { available: false, qty: 0 };
        }

        if (!result.shop) {
          result.shop = { available: false, qty: 0 };
        }

        return result;
      } catch (error) {
        console.error('[MultixStockAvailability] API error:', error);
        return null;
      }
    }

    buildItemHtml(type, label, qty, delivery) {
      const blockId = this.config.blockId;
      const qtyText = `${Math.max(0, qty)} ${this.config.qtySuffix}`;

      return `
        <div class="stock-avail__item-${blockId} stock-avail__item--${type}-${blockId}" data-location-type="${type}">
          <p class="stock-avail__title-${blockId}">
            <strong>${label}</strong>
            <span data-qty="${type}">${qtyText}</span>
          </p>
          <p class="stock-avail__delivery-${blockId}">${delivery}</p>
        </div>`;
    }

    renderAvailability(data) {
      const rigasQty = data?.rigas?.qty ?? 0;
      const shopQty = data?.shop?.qty ?? 0;

      this.innerHTML =
        this.buildItemHtml('rigas', this.config.rigasLabel, rigasQty, this.config.rigasDelivery) +
        this.buildItemHtml('shop', this.config.shopLabel, shopQty, this.config.shopDelivery);
    }

    async fetchAndRender(variantId) {
      if (!variantId) return;

      const data = await this.fetchVariantAvailability(variantId);

      if (data) {
        this.renderAvailability(data);
      }
    }

    handleVariantChange(variantId) {
      if (!variantId || String(variantId) === String(this.dataset.variantId)) return;

      this.dataset.variantId = variantId;

      const variantInfo = document.getElementById(`CurrentVariantInfo__${this.config.sectionId}`);
      if (variantInfo) {
        try {
          const variant = JSON.parse(variantInfo.textContent);
          this.config.inventoryQuantity = parseInt(variant.inventory_quantity, 10) || 0;
          this.dataset.inventoryQuantity = String(this.config.inventoryQuantity);
        } catch (error) {
          console.warn('[MultixStockAvailability] Could not parse variant info', error);
        }
      }

      if (this.config.token) {
        this.fetchAndRender(variantId);
      }
    }

    listenForVariantChanges() {
      if (!this.config.sectionId) return;

      this.abortController?.abort();
      this.abortController = new AbortController();

      document.addEventListener(
        `${PRODUCT_EVENT.updatedVariant}${this.config.sectionId}`,
        (event) => {
          const variantId = event.detail?.currentVariant?.id;
          if (variantId) this.handleVariantChange(variantId);
        },
        { signal: this.abortController.signal }
      );
    }
  }

  if (!customElements.get('multix-stock-availability')) {
    customElements.define('multix-stock-availability', MultixStockAvailability);
  }
}
